<template>
  <modal-component
    :is-displayed="isCreateModalOpened"
    :no-padding="true"
    :title="$t('Sensors.CreateTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <form @submit.prevent="handleSubmit">
      <form-wrapper :is-form-in-modal="true">
        <form-divider>
          <text-box-mask
            :label="$t('Sensors.MAC')"
            mask="XX:XX:XX:XX:XX:XX"
            :maskRules="rules"
            maskInvalidMessage="Inserted address does not match a mac address."
            :inputAttr="{
              'aria-label': 'Mask',
              style: 'text-transform: uppercase',
            }"
            :showClearButton="false"
            valueChangeEvent="keyup"
            :vModel="sensor.mac"
            @value-changed="onValueChange"
            :is-error="v$?.sensor['mac']?.$error"
            :required="true"
          />
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form-wrapper>
    </form>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import InputField from "@/core/Components/FormElements/InputField.vue";
import TextBoxMask from "@/core/Components/FormElements/TextBoxMask.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import SensorService from "@/modules/Parkom/Submodules/Sensors/Services/SensorService";
import { useToast } from "vue-toastification";
import {
  ErrorMessageFormatter,
  formatMacAddress,
  isString,
} from "@/core/Services/GlobalFunctions";
import useValidate from "@vuelidate/core";
import { macAddress, required } from "@vuelidate/validators";

export default defineComponent({
  components: {
    ButtonWrapper,
    FormDivider,
    FormWrapper,
    ModalComponent,
    InputField,
    TextBoxMask
  },
  props: {
    isCreateModalOpened: Boolean,
    refreshData: Function,
  },
  emits: ["toggle-modal"],
  data() {
    return {
      isSubmitLoading: false,
      sensorService: new SensorService(),
      toast: useToast(),
      v$: useValidate(),
      sensor: {
        mac: "",
      },
      rules: {
        X: /[0-9A-Fa-f]/,
      },
    };
  },
  validations() {
    return {
      sensor: {
        mac: {
          required,
          macAddress: macAddress(":"),
        },
      },
    };
  },
  methods: {
    async handleSubmit() {
      if (!this.v$.$error) {
        this.isSubmitLoading = true;
        try {
          await this.sensorService.createSensor({mac: formatMacAddress(this.sensor.mac)});
          this.$emit("toggle-modal");
          this.toast.success("Sensor created successfully!");
          await this.refreshData?.();
        } catch (error: any) {
          if (!isString(error.response.data)) {
            this.toast.error("An unexpected error occurred");
            return;
          }
          this.toast.error(error.response.data);
        } finally {
          this.isSubmitLoading = false;
        }
      }
    },
    onValueChange(e: any) {
      try {
        const previousValue = e.previousValue;
        const newValue = e.value;

        previousValue !== newValue
          ? (this.sensor.mac = newValue)
          : (this.sensor.mac = previousValue);
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        throw error;
      }
    },
  },
});
</script>

<style scoped></style>
